import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import { User, UserLanguage } from '../../interfaces';
import { AuthService } from '../auth';
import { BrowserStorageService } from '../browser-storage';
import { SuperadminService } from '../superadmin';

import { environment } from 'environments/environment';

// HelpScout Beacon attaches itself to the global window object with no bundled types
type BeaconWindow = Window & { Beacon?: (...args: unknown[]) => void };

@Injectable()
export class BeaconService {
    private browserStorageService = inject(BrowserStorageService);
    private authService = inject(AuthService);
    private superadminService = inject(SuperadminService);
    private translateService = inject(TranslateService);

    private currentBeaconId: string;
    private language: UserLanguage;
    private me: User;

    constructor() {
        if (environment.beaconEnabled) {
            // No error handler needed: authService.me() is fed only via .next() (never
            // .error()) and onLangChange is an internal ngx-translate event emitter.
            combineLatest([
                this.authService.me(),
                this.translateService.onLangChange.pipe(distinctUntilChanged((a, b) => a.lang === b.lang)),
            ]).subscribe(([me]) => {
                if (this.superadminService.superadmin()) {
                    this.destroy();
                    this.logout();
                } else {
                    if (!me) {
                        this.destroy();
                        this.logout();
                    }
                    this.me = me;
                    this.language = this.translateService.getCurrentLang() as UserLanguage;

                    const allowedLanguages = [UserLanguage.Estonian, UserLanguage.English, UserLanguage.Russian];
                    if (!allowedLanguages.includes(this.language)) {
                        this.destroy();
                        this.logout();
                        return;
                    }

                    this.init();
                }
            });
        }
    }

    destroy() {
        if (environment.beaconEnabled && 'Beacon' in window) {
            (window as BeaconWindow).Beacon!('destroy');
            this.currentBeaconId = null;
        }
    }

    event() {
        if (environment.beaconEnabled && 'Beacon' in window) {
            (window as BeaconWindow).Beacon!('event', {
                type: 'page-viewed',
                url: document.location.href,
                title: document.title,
            });
            (window as BeaconWindow).Beacon!('suggest');
        }
    }

    private init() {
        if ('Beacon' in window && this.language) {
            const id = this.getId();

            if (id && this.currentBeaconId && this.currentBeaconId !== id) {
                this.destroy();
            }

            this.currentBeaconId = id;

            (window as BeaconWindow).Beacon!('init', id);
            (window as BeaconWindow).Beacon!('config', {
                hideFABOnMobile: true,
            });

            this.identify();
        }
    }

    private identify() {
        if ('Beacon' in window && this.currentBeaconId) {
            if (this.me?.profile) {
                (window as BeaconWindow).Beacon!('identify', {
                    name: this.me.profile?.first_name + ' ' + this.me.profile?.last_name,
                    email: this.me.email,
                    hash: this.me.hash,
                    country: this.me.country?.name,
                    primaryIdentificationValue: this.me.primaryIdentificationValue,
                    birthDate: this.me.profile?.birth_date,
                    language: this.me.settings?.language_abbr,
                });
            } else {
                (window as BeaconWindow).Beacon!('suggest');
            }
        }
    }

    private logout() {
        // https://developer.helpscout.com/beacon-2/web/javascript-api/#beaconlogout
        // According to the docs, 'logout' should clear all data from the Beacon (identify data, Device ID, Conversation history),
        // but nevertheless this data is kept in the local storage after calling 'logout', so we have to remove it manually

        this.browserStorageService.removeBeaconData();
        this.currentBeaconId = null;

        if ('Beacon' in window) {
            (window as BeaconWindow).Beacon!('logout', { endActiveChat: true });
        }
    }

    private getId(): string {
        return environment.beaconIds[this.language] || environment.beaconIds[UserLanguage.English];
    }
}
